import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { useCallback } from "react";
import { MuiImageComponent } from "./mui-image-component";
import { $isVideoNode, VideoNode } from "./video-node";
import { VideoTitle } from "./video-title";
import { convertVideoElement, isVideoElement } from "./video/export";

import type { UUID } from "../../../../utils/uuid";
import type { MuiImageComponentProps } from "./mui-image-component";
import type { SerializedVideoNode } from "./video-node";
import type { DOMConversionMap, LexicalNode } from "lexical";
import type { FC } from "react";

const VideoNodeComponent: FC<MuiImageComponentProps & { title?: string; assetId?: UUID }> = (
  props,
) => {
  const [editor] = useLexicalComposerContext();

  const handleSetTitle = useCallback(
    (newTitle: string | undefined) => {
      editor.update(() => {
        const node = $getNodeByKey(props.nodeKey);
        if ($isVideoNode(node)) {
          node.setTitle(newTitle);
        }
      });
    },
    [props.nodeKey, editor],
  );

  return (
    <>
      {props.assetId && (
        <VideoTitle
          {...props}
          width={`${props.proportionOfMaxWidth * 100}%`}
          asset_id={props.assetId}
          setNodeTitle={handleSetTitle}
        />
      )}
      {/*  TODO: GRO-626 this only shows the thumbnail. We should show/link to the actual video somewhere */}
      <MuiImageComponent {...props} />
    </>
  );
};

export class VideoNodeMui extends VideoNode {
  __currentlyResizing = false;
  decorate(): React.JSX.Element {
    return (
      <VideoNodeComponent
        nodeKey={this.__key}
        aspect={this.__aspect}
        proportionOfMaxWidth={this.__proportionOfMaxWidth}
        src={this.__thumbnailUrl}
        format={this.__format}
        resizingAllowed={() => ({ allowed: true })}
        title={this.__title}
        assetId={this.__sourceAssetId}
      />
    );
  }
  static importJSON(serializedNode: SerializedVideoNode): VideoNode {
    return super.importJSON(serializedNode);
  }
  exportJSON(): SerializedVideoNode {
    return super.exportJSON();
  }
  static clone(node: VideoNode): VideoNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  setIsCurrentlyResizing(isCurrentlyResizing: boolean): void {
    const writable = this.getWritable();
    writable.__currentlyResizing = isCurrentlyResizing;
  }

  getIsCurrentlyResizing(): boolean {
    return this.__currentlyResizing;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (node: HTMLElement) => {
        if (!isVideoElement(node)) return null;

        return {
          conversion: (x) => convertVideoElement(x, VideoNodeMui),
          priority: 1,
        };
      },
    };
  }
}

export function $createVideoNode(
  headless: boolean,
  videoUrl: string,
  thumbnailUrl: string,
  aspect: number,
  proportionOfMaxWidth: number,
  sourceAssetId: UUID,
  title?: string,
): VideoNode {
  return new (headless ? VideoNode : VideoNodeMui)(
    videoUrl,
    thumbnailUrl,
    aspect,
    proportionOfMaxWidth,
    title,
    sourceAssetId,
  );
}

export function $isVideoNodeMui(node: LexicalNode | null | undefined): node is VideoNodeMui {
  return node instanceof VideoNodeMui;
}
